
// src/QuestionTable.js
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";

const QuestionTable = () => {
  const [topics, setTopics] = useState([]); // [{id, title, questions: []}]
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const [editingRow, setEditingRow] = useState(null); // { topicId, questionId }
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");

  // Load topics + questions from Firestore
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const colRef = collection(db, "interview");
        const snap = await getDocs(colRef);

        const items = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            title: data.title || d.id.toUpperCase(),
            description: data.description || "",
            questions: Array.isArray(data.questions) ? data.questions : [],
          };
        });

        // sort topics by title
        items.sort((a, b) =>
          a.title.toLowerCase().localeCompare(b.title.toLowerCase())
        );

        setTopics(items);
        if (items.length > 0) {
          setSelectedTopicId(items[0].id);
        }
      } catch (err) {
        console.error("Error loading topics:", err);
        setStatus("Error loading topics from Firestore.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const currentTopic = topics.find((t) => t.id === selectedTopicId) || null;
  const currentQuestions = currentTopic?.questions || [];

  const startEdit = (topicId, q) => {
    setEditingRow({ topicId, questionId: q.id || null, index: q.order });
    setEditQuestion(q.question || "");
    setEditAnswer(q.answer || "");
    setStatus("");
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditQuestion("");
    setEditAnswer("");
  };

  const saveEdit = async () => {
    if (!editingRow || !currentTopic) return;

    const { topicId, questionId, index } = editingRow;

    const topic = topics.find((t) => t.id === topicId);
    if (!topic) return;

    const updatedQuestions = topic.questions.map((q, i) => {
      const matchById = questionId && q.id === questionId;
      const matchByIndex =
        !questionId && typeof q.order === "number" && q.order === index;

      if (matchById || matchByIndex) {
        return {
          ...q,
          question: editQuestion,
          answer: editAnswer,
        };
      }
      return q;
    });

    try {
      setStatus("Saving changes...");
      const topicRef = doc(collection(db, "interview"), topicId);
      await setDoc(
        topicRef,
        {
          questions: updatedQuestions,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      // Update local state
      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId ? { ...t, questions: updatedQuestions } : t
        )
      );

      setStatus("Row updated successfully.");
      cancelEdit();
    } catch (err) {
      console.error("Error saving edit:", err);
      setStatus("Error saving changes to Firestore.");
    }
  };

  const deleteRow = async (topicId, q) => {
    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }

    const topic = topics.find((t) => t.id === topicId);
    if (!topic) return;

    const filteredQuestions = topic.questions.filter((item) => {
      if (q.id) {
        return item.id !== q.id;
      }
      // fallback by order if no id
      return item.order !== q.order;
    });

    try {
      setStatus("Deleting row...");
      const topicRef = doc(collection(db, "interview"), topicId);
      await setDoc(
        topicRef,
        {
          questions: filteredQuestions,
          totalQuestions: filteredQuestions.length,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId ? { ...t, questions: filteredQuestions } : t
        )
      );
      setStatus("Row deleted successfully.");
    } catch (err) {
      console.error("Error deleting row:", err);
      setStatus("Error deleting row from Firestore.");
    }
  };

  return (
    <div className="col-md-10 container  py-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="h4 mb-0">Interview Questions – Admin Table</h2>
        {loading && (
          <span className="badge text-bg-secondary">Loading topics...</span>
        )}
      </div>

      {/* Topic selector */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Select Topic</label>
          <select
            className="form-select"
            value={selectedTopicId}
            onChange={(e) => {
              setSelectedTopicId(e.target.value);
              cancelEdit();
              setStatus("");
            }}
          >
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-muted">Loading topics...</p>
      ) : !currentTopic ? (
        <p className="text-muted">No topic selected.</p>
      ) : (
        <>
          {currentTopic.description && (
            <div className="alert alert-info py-2">
              <strong>Description:&nbsp;</strong>
              <span>{currentTopic.description}</span>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-bordered table-striped table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "5%" }}>#</th>
                  {/* <th style={{ width: "15%" }}>Topic</th> */}
                  <th style={{ width: "25%" }}>Question</th>
                  <th>Answer</th>
                  <th style={{ width: "15%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentQuestions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No questions available for this topic.
                    </td>
                  </tr>
                ) : (
                  currentQuestions
                    .slice()
                    .sort((a, b) => {
                      if (
                        typeof a.order === "number" &&
                        typeof b.order === "number"
                      ) {
                        return a.order - b.order;
                      }
                      return (a.question || "")
                        .toLowerCase()
                        .localeCompare((b.question || "").toLowerCase());
                    })
                    .map((q, index) => {
                      const isEditing =
                        editingRow &&
                        editingRow.topicId === currentTopic.id &&
                        (editingRow.questionId
                          ? editingRow.questionId === q.id
                          : editingRow.index === q.order);

                      return (
                        <tr key={q.id || index}>
                          <td>{index + 1}</td>
                          {/* <td>{q.topic || currentTopic.title}</td> */}
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editQuestion}
                                onChange={(e) => setEditQuestion(e.target.value)}
                                className="form-control form-control-sm"
                              />
                            ) : (
                              q.question
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <textarea
                                value={editAnswer}
                                onChange={(e) => setEditAnswer(e.target.value)}
                                className="form-control form-control-sm"
                                rows={4}
                              />
                            ) : (
                              <div style={{ whiteSpace: "pre-wrap" }}>
                                {q.answer}
                              </div>
                            )}
                          </td>
                          <td>
                            {/* Stack on mobile, inline on md+ */}
                            <div className="d-flex flex-column flex-md-row gap-2">
                              {isEditing ? (
                                <>
                                  <button
                                    onClick={saveEdit}
                                    className="btn btn-success btn-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="btn btn-secondary btn-sm"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() =>
                                      startEdit(currentTopic.id, q)
                                    }
                                    className="btn btn-primary btn-sm"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() =>
                                      deleteRow(currentTopic.id, q)
                                    }
                                    className="btn btn-danger btn-sm"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {status && (
        <div className="mt-3">
          <small className="text-muted">{status}</small>
        </div>
      )}
    </div>
  );
};

export default QuestionTable;

